import {
  Anchor,
  AspectRatio,
  Badge,
  Box,
  Center,
  Image,
  Title,
  UnstyledButton,
  useMantineColorScheme,
  useMatches,
} from "@mantine/core";

import classes from "./CarCard.module.css";

import carburateLogo from "@/assets/illustrations/carburate-logo-small.svg";
import { LoadedImage, MakeCard } from "@/components/elements";
import { env } from "@/config/env";
import { useCarImages } from "@/services/cars/get-car-images";
import { useMarketCarImages } from "@/services/market/get-market-car-images";
import { useProfile } from "@/services/profile/get-profile";

import type { MakeName } from "@/types/make";
import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";

export type CarCardProps = {
  badge?: string;
  make: MakeName;
  name: string;
  onClick?: () => void;
  carId: string;
  marketCarId: string;
} & MantineStyleProps;

export const CarCard: FC<CarCardProps> = ({
  badge,
  make,
  name,
  onClick,
  carId,
  marketCarId,
  ...props
}) => {
  const isMobile = useMatches({ base: true, sm: false, md: false });

  /* Theme */
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const { data: gallery } = useCarImages({ carId });
  const { data: marketGallery } = useMarketCarImages({
    carId: marketCarId,
  });
  const { data: profile, isLoading: profileIsLoading } = useProfile();
  const hideStockImages =
    profileIsLoading || profile?.additional_info.stockImagesHiddenCarIds?.includes(carId);
  const image = gallery?.results?.[0] || (!hideStockImages && marketGallery?.results?.[0]);
  const fontSize = { base: "sm", md: "md" };

  const bgColor = isDark ? "#323232" : "#d9d9d9";

  return (
    <UnstyledButton classNames={classes} onClick={onClick}>
      <Box
        {...props}
        pos="relative"
        style={{
          isolation: "isolate",
        }}
      >
        <Box
          pos="absolute"
          top={0}
          left={0}
          m="md"
          style={{
            zIndex: 2,
          }}
        >
          <MakeCard
            pos="absolute"
            top={0}
            left={0}
            m="md"
            make={make}
            p="xs"
            w={40}
            h={40}
            hideBG
          />
        </Box>

        {badge && (
          <Badge
            pos="absolute"
            top={0}
            right={0}
            m="md"
            color="primary"
            variant="default"
            maw="62%"
            style={{
              zIndex: 2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {badge}
          </Badge>
        )}

        <AspectRatio ratio={16 / 9}>
          <Box
            style={{
              overflow: "hidden",
              borderRadius: "var(--mantine-radius-md)",
            }}
          >
            {image && image.path.includes(env.API_URL) ? (
              <LoadedImage image={image} className={classes.image} />
            ) : image ? (
              <Image
                src={image.path}
                alt={image.name}
                radius="md"
                className={classes.image}
                fit="cover"
              />
            ) : (
              /* Car Image Placeholder */
              <Center bg={bgColor} bdrs="md">
                <Image
                  src={carburateLogo}
                  w="auto"
                  height={isMobile ? "70%" : "60%"}
                  alt="Carburate"
                />
              </Center>
            )}
          </Box>
        </AspectRatio>

        <Box p="md">
          <Title
            order={4}
            component={Anchor}
            fw={500}
            fz={fontSize}
            lineClamp={1}
            title={`${make} ${name}`}
          >{`${make} ${name}`}</Title>
        </Box>
      </Box>
    </UnstyledButton>
  );
};
